import client from "services/ngage/api-client";
import {Offer, OfferData} from "types/Offer";
import {deserializeOffer} from "services/ngage/deserializers/offer";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";
import {OfferDistribution, OfferDistributionData} from "../../types/OfferDistribution";
import {deserializeOfferDistribution} from "./deserializers/offer-distribution";
import {parameters} from "../string";

export type OfferFilters = {
  search?: string | null
  page?: number
  perPage?: number
  owner?: number
}

/**
 * Get all offers (paginated)
 */
const all = async (page: number = 1) => {
  const response = await client.get<DataResource<Offer> | null>(`/offers?page=${page}`)

  if (response.data) {
    response.data.data = response.data.data.map(deserializeOffer)
  }

  return response.data
}

/**
 * Get all offers that match the given filters (paginated)
 */
const filtered = async (filters: OfferFilters = {}) => {
  const params = parameters(filters, {page: 1, per_page: 100});
  const response = await client.get<DataResource<Offer> | null>('/offers', {params})

  if (response.data) {
    response.data.data = response.data.data.map(deserializeOffer)
  }

  return response.data
}

/**
 * Get a single offer
 *
 * @param {number} id
 */
const get = async (id: number) => {
  const response = await client.get<Offer>(`/offers/${id}`)
  return response.data ? deserializeOffer(response.data) : null
}

/**
 * Store a new offer
 *
 * @param data
 */
const store = async (data: OfferData) => {
  const response = await client.post<Offer>('/offers', data)
  return response.data ? deserializeOffer(response.data) : null
}

/**
 * Update an existing offer
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: OfferData) => {
  const response = await client.put<Offer>(`/offers/${id}`, data)
  return response.data ? deserializeOffer(response.data) : null
}

/**
 * Permanently delete a offer
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
  const response = await client.delete<null>(`/offers/${id}`)
  return response.status === 204
}

/**
 * Distribute an offer
 *
 * @param {number} id
 * @param {OfferDistributionData} data
 */
const distribute = async (id: number, data: OfferDistributionData) => {
  const response = await client.post<OfferDistribution>(`/offers/${id}/distributions`, data)
  return response.data ? deserializeOfferDistribution(response.data) : null
}

const offers = {
  all,
  filtered,
  get,
  store,
  update,
  destroy,
  distribute,
}

export default offers;